import React from 'react';
import { Drawer, Box, IconButton, Typography, Divider, Table, TableBody, TableCell, TableRow, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { PickingInfo } from '@deck.gl/core';
import type { GeoJsonFeature } from '../../utils/ogc';
import SensorDetailContent from './SensorDetails';
import SwipeableEdgeDrawer from './SwipeableEdgeDrawer';
import { motion, transform, useMotionValue, useSpring } from 'framer-motion';

const MotionPaper = motion.create(Paper);

export const ResponsiveDrawer = ({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return isSmall ? (
    <SwipeableEdgeDrawer open={open} setOpen={onClose}>
      {children}
    </SwipeableEdgeDrawer>
  ) : (
    <SideDrawer open={open} onClose={onClose}>
      {children}
    </SideDrawer>
  );
}
export default ResponsiveDrawer;


export const SideDrawer = ({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Drawer
      anchor="right"
      elevation={6}
      open={open}
      onClose={onClose}
      ModalProps={{ hideBackdrop: true, keepMounted: true }}
      sx={{ pointerEvents: 'none', overflowX: 'visible' }}
      keepMounted={false}
      slotProps={{
        paper: {
          sx: {
            pointerEvents: 'auto',
            width: { xs: 360, sm: expanded ? 1200 : 420 },
            transition: 'width 0.3s ease',
            maxWidth: 'calc(100vw - 32px)',
            height: 'auto',
            bottom: 0,
            // bgcolor: 'background.opaque',
            bgcolor: 'transparent',
            boxShadow: 12,
            // borderRadius: 6,
            // m: { xs: 1.5, sm: 2 },
            // my: { xs: 2, sm: 3 },

            m: 0,
            // borderRadius: '8px 0 0 8px',

            // mt: { xs: 8, sm: 10 },
            // mb: { xs: 2, sm: 3 },
            // overflowY: 'auto',
            // overflowX: 'visible',
            overflow: 'visible',
          }
        }
      }}
      >
        <IconButton
          onClick={() => setExpanded(x => !x)}
          sx={{ position: 'absolute', top: '50%', left: 0, transform: 'translate(-50%, -50%) rotate(90deg)', zIndex: 10, bgcolor: 'background.paper', boxShadow: 1 }}
          size="small"
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        {children}
      {/* {children?.({ sheetState, setSheetState })} */}
    </Drawer>
  );
}


export const SwipeableDrawer = ({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) => {
  const [sheetState, setSheetState] = React.useState<'collapsed' | 'expanded'>('collapsed');
  const y = useMotionValue(0);
  const [viewportH, setViewportH] = React.useState<number>(typeof window !== 'undefined' ? window.innerHeight : 800);
  React.useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const collapsedHeight = 88;
  const expandedHeight = Math.round(viewportH);
  const snapThreshold = Math.round((collapsedHeight + expandedHeight) / 2);

  // Simpler, more stable drag model without framer-motion side-effects
  const [height, setHeight] = React.useState<number>(collapsedHeight);
  const startHeightRef = React.useRef<number>(collapsedHeight);
  const startYRef = React.useRef<number>(0);
  const draggingRef = React.useRef(false);

  React.useEffect(() => {
    setHeight(sheetState === 'expanded' ? expandedHeight : collapsedHeight);
  }, [sheetState, expandedHeight]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // ModalProps={{ keepMounted: true }}
      sx={{
        width: '100vw',
        // height: '100vh',
      }}
      slots={{
        paper: MotionPaper,
      }}
      keepMounted={false}
      elevation={6}
      slotProps={{
        backdrop: {
          sx: { display: 'none' }
        },
        paper: {
          style: { height },
          sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            // inset: 'auto 0 0 0',
            width: '100vw',
            maxWidth: '100vw',
            // Allow vertical panning inside content for mobile scrolling
            touchAction: 'pan-y',
            pointerEvents: 'auto',
            bgcolor: 'background.opaque',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            boxShadow: 12,
            transition: draggingRef.current ? 'none' : 'height 160ms cubic-bezier(.2,.8,.2,1)'
          }
        },
      }}
    >
      {/* Large drag zone across the top portion of the sheet */}
      <Box
        sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120, cursor: 'grab', zIndex: 11, background: 'transparent' }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
          startYRef.current = e.clientY;
          startHeightRef.current = height;
          const startTime = performance.now();
          let lastY = e.clientY;
          let lastT = startTime;
          const onMove = (ev: PointerEvent) => {
            const dy = ev.clientY - startYRef.current;
            const next = Math.max(collapsedHeight, Math.min(expandedHeight, startHeightRef.current - dy));
            setHeight(next);
            lastY = ev.clientY;
            lastT = performance.now();
          };
          const onUp = () => {
            draggingRef.current = false;
            // Determine velocity to bias snap decision
            const totalDy = lastY - startYRef.current; // + down, - up
            const totalDt = Math.max(1, performance.now() - startTime);
            const vy = totalDy / totalDt; // px per ms
            const bias = 0.5; // px/ms threshold
            if (vy <= -bias) {
              setSheetState('expanded');
            } else if (vy >= bias) {
              setSheetState('collapsed');
            } else {
              const finalH = height;
              setSheetState(finalH > snapThreshold ? 'expanded' : 'collapsed');
            }
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
        }}
      />
      {/* Puller / Header drag target */}
      <Box
        sx={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', width: 44, height: 6, borderRadius: 3, bgcolor: 'divider', cursor: 'grab', zIndex: 10 }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
          startYRef.current = e.clientY;
          startHeightRef.current = height;
          const onMove = (ev: PointerEvent) => {
            const dy = ev.clientY - startYRef.current;
            const next = Math.max(collapsedHeight, Math.min(expandedHeight, startHeightRef.current - dy));
            setHeight(next);
          };
          const onUp = () => {
            draggingRef.current = false;
            const finalH = height;
            setSheetState(finalH > snapThreshold ? 'expanded' : 'collapsed');
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
        }}
        onDoubleClick={() => setSheetState(x => (x === 'expanded' ? 'collapsed' : 'expanded'))}
      />
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
        onWheel={(e) => {
          // If user scrolls up at top and sheet is expanded, allow pull-to-collapse
          const el = e.currentTarget as HTMLDivElement;
          const atTop = el.scrollTop <= 0;
          if (atTop && e.deltaY < 0) {
            e.preventDefault();
            const next = Math.max(collapsedHeight, Math.min(expandedHeight, height + e.deltaY));
            setHeight(next);
            if (next <= snapThreshold * 0.9) setSheetState('collapsed');
          }
        }}
      >
        {children}
        {/* {children?.({ sheetState, setSheetState })} */}
      </Box>
    </Drawer>
  );
}
